import { useEffect, useRef, useState } from 'react';
import { DatePicker } from 'risa-oasis-ui_v2';
import { formatDisplayDate, parseIsoCalendarDate } from '../../../utils/displayDate';

interface DateRangeSelectorProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
}

/**
 * Format a `Date` as a local `YYYY-MM-DD` string.
 *
 * @param date - The date to format.
 * @returns The ISO date string.
 */
function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Roll-up date selector for the notifications view, backed by the oasis
 * `DatePicker`. The calendar is hidden behind a trigger button and only shown
 * in a popover when clicked; it closes on outside click or after a date is
 * picked. Adapts the component's `Date` API to the string date the parent page
 * controls.
 *
 * @param props - {@link DateRangeSelectorProps}
 * @returns The date selector
 */
export function DateRangeSelector({ selectedDate, onDateChange }: DateRangeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border outline-none focus:ring-2 transition-colors"
        style={{
          backgroundColor: 'var(--color-input-bg)',
          borderColor: 'var(--color-input-border)',
          color: 'var(--color-text-primary)',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M5 1v3M11 1v3M2 6h12M3 3h10a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {formatDisplayDate(selectedDate, 'Select date')}
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 'auto' }}>
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="dialog"
          className="absolute right-0 mt-2 z-50 rounded-md border shadow-lg p-2"
          style={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'var(--color-border)',
          }}
        >
          <DatePicker
            initialDate={parseIsoCalendarDate(selectedDate)}
            onDateChange={(date) => {
              if (date) {
                onDateChange(toIsoDate(date));
                setIsOpen(false);
              }
            }}
          />
        </div>
      )}
    </div>
  );
}
